import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTokens } from '../contexts/TokenContext';
import { useProfileStore } from '../stores/profileStore';
import { AvatarFrame, JourneyPath, AchievementCard, CustomizationSheet } from '../components/progression';
import type { FrameType } from '../components/progression/AvatarFrame';
import * as progressionService from '../services/progressionService';
import type {
    LevelInfo,
    MilestoneInfo,
    LeaderboardEntry,
    LeaderboardPosition,
    UserTitle,
    AvatarFrame as AvatarFrameType,
} from '../services/progressionService';
import './ProgressionPage.css';

/** Format learning time (seconds → hours/minutes) — API returns duration_seconds */
const formatLearningTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins === 0 ? `${hours}h` : `${hours}h ${mins}m`;
};

/** SVG medal icons for leaderboard top 3 */
const MedalIcon: React.FC<{ rank: 1 | 2 | 3 }> = ({ rank }) => {
    const colors = {
        1: { fill: '#DAA520', stroke: '#B8860B', ribbon: '#DC2626' },
        2: { fill: '#A8A8A8', stroke: '#808080', ribbon: '#3B82F6' },
        3: { fill: '#B87333', stroke: '#8B5E3C', ribbon: '#16A34A' },
    };
    const c = colors[rank];
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-label={`Rank ${rank} medal`}>
            <circle cx="12" cy="14" r="7" fill={c.fill} stroke={c.stroke} strokeWidth="1.5" />
            <path d="M9 2L12 8L15 2" stroke={c.ribbon} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            <text x="12" y="17" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#fff">{rank}</text>
        </svg>
    );
};

/** SVG gift icon for milestone rewards */
const GiftIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
        <rect x="3" y="8" width="18" height="13" rx="2" />
        <path d="M12 8v13" />
        <path d="M3 13h18" />
        <path d="M12 8C12 8 12 4 9 4C6 4 6 8 12 8Z" />
        <path d="M12 8C12 8 12 4 15 4C18 4 18 8 12 8Z" />
    </svg>
);

/** SVG checkmark icon */
const CheckIcon: React.FC = () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 8.5L6.5 12L13 4" />
    </svg>
);

/** SVG lock icon */
const LockIcon: React.FC = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="11" width="18" height="11" rx="2" />
        <path d="M7 11V7a5 5 0 0110 0v4" />
    </svg>
);

const ProgressionPage: React.FC = () => {
    const { t } = useTranslation('progression');
    const { user } = useAuth();
    const { refreshStats } = useTokens();
    const { stats: profileStats, fetchStats } = useProfileStore();

    // State
    const [levelInfo, setLevelInfo] = useState<LevelInfo | null>(null);
    const [milestones, setMilestones] = useState<MilestoneInfo[]>([]);
    const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
    const [leaderboardPosition, setLeaderboardPosition] = useState<LeaderboardPosition | null>(null);
    const [weekDates, setWeekDates] = useState<{ start: string; end: string } | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [claimingMilestone, setClaimingMilestone] = useState<number | null>(null);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
    const [customizeOpen, setCustomizeOpen] = useState(false);
    const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
        };
    }, []);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const [level, milestoneData, leaderboardData, positionData] = await Promise.all([
                progressionService.getLevelInfo(),
                progressionService.getMilestones(),
                progressionService.getWeeklyLeaderboard({ limit: 50 }),
                progressionService.getLeaderboardPosition(),
            ]);
            setLevelInfo(level);
            setMilestones(milestoneData);
            setLeaderboard(leaderboardData.rankings);
            setLeaderboardPosition(positionData);
            setWeekDates({
                start: new Date(leaderboardData.weekStartDate).toLocaleDateString(),
                end: new Date(leaderboardData.weekEndDate).toLocaleDateString(),
            });
        } catch (err) {
            console.error('Failed to load progression data:', err);
            setError(t('error'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { loadData(); }, [loadData]);

    // Fetch profile stats
    useEffect(() => {
        if (user?.id) {
            fetchStats(user.id);
        }
    }, [user?.id, fetchStats]);

    const showToast = (message: string, type: 'success' | 'error') => {
        if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
        setToast({ message, type });
        toastTimeoutRef.current = setTimeout(() => setToast(null), 3000);
    };

    const handleClaimMilestone = async (xpThreshold: number) => {
        try {
            setClaimingMilestone(xpThreshold);
            const result = await progressionService.claimMilestone(xpThreshold);
            showToast(t('milestones.claimSuccess', { tokens: result.tokens }), 'success');
            const [newMilestones] = await Promise.all([
                progressionService.getMilestones(),
                refreshStats(),
            ]);
            setMilestones(newMilestones);
        } catch (err) {
            console.error('Failed to claim milestone:', err);
            showToast(t('milestones.claimError'), 'error');
        } finally {
            setClaimingMilestone(null);
        }
    };

    const handleTitleChange = (title: UserTitle) => {
        setLevelInfo(prev => prev ? { ...prev, activeTitle: title } : null);
    };

    const handleFrameChange = (frame: AvatarFrameType) => {
        setLevelInfo(prev => prev ? { ...prev, activeFrame: frame } : null);
    };

    if (loading) {
        return (
            <div className="progression-page">
                <div className="progression-loading">
                    <div className="loading-spinner" />
                    <p>{t('loading')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="progression-page">
                <div className="progression-error">
                    <p>{error}</p>
                    <button onClick={loadData}>{t('retry')}</button>
                </div>
            </div>
        );
    }

    if (!levelInfo) return null;

    const nextMilestone = milestones.find(m => !m.claimed && m.claimable) || milestones.find(m => !m.claimed);
    const claimableMilestones = milestones.filter(m => m.claimable && !m.claimed);
    const xpToNextMilestone = nextMilestone ? nextMilestone.xpThreshold - levelInfo.totalXp : 0;

    return (
        <div className="progression-page-bg">
        <div className="progression-page">
            {/* Toast */}
            {toast && (
                <div className={`progression-toast ${toast.type}`} role="alert" aria-live="polite">
                    {toast.message}
                </div>
            )}

            {/* ========== Zone 1: Your Level ========== */}
            <section className="progression-zone">
                <AchievementCard
                    levelInfo={levelInfo}
                    avatarUrl={user?.avatarUrl || undefined}
                    displayName={user?.username || undefined}
                    onAvatarClick={() => setCustomizeOpen(true)}
                />

                {/* Inline stats row — always visible, no toggle */}
                <div className="progression-stats-row">
                    <div className="progression-stat">
                        <span className="progression-stat-value">{profileStats?.coursesEnrolled ?? 0}</span>
                        <span className="progression-stat-label">{t('stats.courses')}</span>
                    </div>
                    <div className="progression-stat">
                        <span className="progression-stat-value">{formatLearningTime(profileStats?.totalLearningTime ?? 0)}</span>
                        <span className="progression-stat-label">{t('stats.learningTime')}</span>
                    </div>
                    <div className="progression-stat">
                        <span className="progression-stat-value">{profileStats?.totalFollowers ?? 0}</span>
                        <span className="progression-stat-label">{t('stats.followers')}</span>
                    </div>
                    <div className="progression-stat">
                        <span className="progression-stat-value">{progressionService.formatXp(levelInfo.weeklyXp)}</span>
                        <span className="progression-stat-label">{t('stats.weeklyXp')}</span>
                    </div>
                </div>

                {/* Next milestone hint integrated into Zone 1 */}
                {nextMilestone && !nextMilestone.claimable && xpToNextMilestone > 0 && (
                    <p className="progression-next-milestone-hint">
                        {t('milestones.nextDistance', {
                            xp: progressionService.formatXp(xpToNextMilestone),
                        })}
                    </p>
                )}

                {/* Claimable milestone alert + claim button */}
                {claimableMilestones.length > 0 && (
                    <div className="progression-claimable-banner">
                        <GiftIcon className="claimable-banner-icon" />
                        <span>{t('milestones.claimableCount', { count: claimableMilestones.length })}</span>
                        {claimableMilestones.map((m) => (
                            <button
                                key={m.xpThreshold}
                                className="claimable-banner-btn"
                                onClick={() => handleClaimMilestone(m.xpThreshold)}
                                disabled={claimingMilestone === m.xpThreshold}
                            >
                                {claimingMilestone === m.xpThreshold ? '...' : `${t('milestones.claim')} +${m.tokens}`}
                            </button>
                        ))}
                    </div>
                )}
            </section>

            {/* ========== Zone 2: Your Journey ========== */}
            <section className="progression-zone">
                <JourneyPath
                    currentLevel={levelInfo.level}
                    unlockedTitles={levelInfo.unlockedTitles}
                    unlockedFrames={levelInfo.unlockedFrames}
                    avatarUrl={user?.avatarUrl || undefined}
                />

                {/* Milestones list — always visible, no expandable toggle */}
                <div className="milestones-grid">
                    {milestones.map((milestone) => (
                        <div
                            key={milestone.xpThreshold}
                            className={`milestone-card ${milestone.claimed ? 'claimed' : milestone.claimable ? 'claimable' : 'locked'}`}
                        >
                            <div className="milestone-xp">
                                {t('milestones.xpRequired', { xp: progressionService.formatXp(milestone.xpThreshold) })}
                            </div>
                            <div className="milestone-tokens">
                                {t('milestones.tokens', { tokens: milestone.tokens })}
                            </div>
                            <div className="milestone-action">
                                {milestone.claimed ? (
                                    <span className="milestone-claimed">
                                        <CheckIcon /> {t('milestones.claimed')}
                                    </span>
                                ) : milestone.claimable ? (
                                    <button
                                        className="milestone-claim-btn"
                                        onClick={() => handleClaimMilestone(milestone.xpThreshold)}
                                        disabled={claimingMilestone === milestone.xpThreshold}
                                    >
                                        {claimingMilestone === milestone.xpThreshold ? '...' : t('milestones.claim')}
                                    </button>
                                ) : (
                                    <span className="milestone-locked">
                                        <LockIcon /> {t('milestones.locked')}
                                    </span>
                                )}
                            </div>
                            <div className="milestone-progress">
                                <div
                                    className="milestone-progress-bar"
                                    style={{
                                        width: milestone.claimed
                                            ? '100%'
                                            : `${Math.min(100, (levelInfo.totalXp / milestone.xpThreshold) * 100)}%`,
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ========== Zone 3: This Week ========== */}
            <section className="progression-zone">
                <h2 className="zone-title">{t('leaderboard.sectionTitle')}</h2>
                <p className="section-subtitle">{t('leaderboard.subtitle')}</p>
                {weekDates && (
                    <p className="week-dates">{t('leaderboard.weekOf', { start: weekDates.start, end: weekDates.end })}</p>
                )}

                {leaderboardPosition && (
                    <div className="user-position-card">
                        <div className="user-position-info">
                            <span className="position-label">{t('leaderboard.yourPosition')}</span>
                            <span className="position-rank">
                                {leaderboardPosition.rank ? `#${leaderboardPosition.rank}` : t('leaderboard.noRank')}
                            </span>
                        </div>
                        <div className="user-position-stats">
                            <div className="position-stat">
                                <span className="stat-value">{leaderboardPosition.totalPoints}</span>
                                <span className="stat-label">{t('leaderboard.points')}</span>
                            </div>
                            <div className="position-stat">
                                <span className="stat-value">{leaderboardPosition.questsCompleted}</span>
                                <span className="stat-label">{t('leaderboard.quests')}</span>
                            </div>
                            <div className="position-stat">
                                <span className="stat-value">{leaderboardPosition.totalParticipants}</span>
                                <span className="stat-label">{t('leaderboard.totalParticipants')}</span>
                            </div>
                        </div>
                    </div>
                )}

                <div className="leaderboard-table">
                    <div className="leaderboard-header">
                        <span className="col-rank">{t('leaderboard.rank')}</span>
                        <span className="col-player">{t('leaderboard.player')}</span>
                        <span className="col-points">{t('leaderboard.points')}</span>
                        <span className="col-quests">{t('leaderboard.quests')}</span>
                    </div>
                    <div className="leaderboard-body">
                        {leaderboard.length === 0 ? (
                            <div className="leaderboard-empty">
                                <p>{t('leaderboard.noRank')}</p>
                                <p className="leaderboard-empty-hint">{t('leaderboard.emptyHint')}</p>
                            </div>
                        ) : (
                            leaderboard.map((entry) => (
                                <div
                                    key={entry.userId}
                                    className={`leaderboard-row ${entry.userId === user?.id ? 'is-user' : ''} ${entry.rank <= 3 ? `rank-${entry.rank}` : ''}`}
                                >
                                    <span className="col-rank">
                                        {entry.rank <= 3 ? (
                                            <MedalIcon rank={entry.rank as 1 | 2 | 3} />
                                        ) : (
                                            `#${entry.rank}`
                                        )}
                                    </span>
                                    <span className="col-player">
                                        <AvatarFrame
                                            frame={(entry.userId === user?.id ? levelInfo.activeFrame : 'none') as FrameType}
                                            avatarUrl={entry.avatarUrl || undefined}
                                            size={32}
                                        />
                                        <span className="player-name">
                                            {entry.displayName || 'Player'}
                                            {entry.userId === user?.id && (
                                                <span className="you-badge">{t('leaderboard.you')}</span>
                                            )}
                                        </span>
                                        <span className="player-level">Lv.{entry.level}</span>
                                    </span>
                                    <span className="col-points">{entry.totalPoints.toLocaleString()}</span>
                                    <span className="col-quests">{entry.questsCompleted}</span>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Rewards summary — compact single line */}
                <p className="leaderboard-rewards-summary">{t('rewards.summary')}</p>
            </section>

            {/* Customization Sheet */}
            <CustomizationSheet
                isOpen={customizeOpen}
                onClose={() => setCustomizeOpen(false)}
                levelInfo={levelInfo}
                avatarUrl={user?.avatarUrl || undefined}
                onTitleChange={handleTitleChange}
                onFrameChange={handleFrameChange}
            />
        </div>
        </div>
    );
};

export default ProgressionPage;
